import { z } from "zod";
import { pageSchema } from "@/helpers/types";

// A skill is a directory: SKILL.md plus optional references/ and scripts/.
// The list endpoint returns metadata only — bundle content lives in S3 and is
// fetched on the detail view, so `content` is absent from the summary.
export const SkillFileSchema = z.object({
  path: z.string(),
  content: z.string(),
});
export type SkillFile = z.infer<typeof SkillFileSchema>;

export const SkillPrivacySchema = z.enum(["private", "public"]);
export type SkillPrivacy = z.infer<typeof SkillPrivacySchema>;

/** One file in a bundle, as the detail drawer lists it. */
export const SkillFileMetaSchema = z.object({
  path: z.string(),
  size: z.number(),
});
export type SkillFileMeta = z.infer<typeof SkillFileMetaSchema>;

/**
 * Where an imported skill was copied from. Absent on uploads.
 *
 * An import is a copy, not a link, so this is the only record of the origin —
 * and it is matched on `path`, which means it survives the user renaming their
 * copy.
 */
export const SkillSourceRefSchema = z.object({
  repo: z.string(),
  path: z.string(),
  commit_sha: z.string(),
  imported_at: z.string().nullable().optional(),
});
export type SkillSourceRef = z.infer<typeof SkillSourceRefSchema>;

export const SkillSummarySchema = z.object({
  public_id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  privacy: SkillPrivacySchema,
  // A count, not the inventory: the list renders name/description/updated/
  // author/scope, and the paths and sizes are a detail-view concern.
  file_count: z.number(),
  source_ref: SkillSourceRefSchema.nullable().optional(),
  created_by: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SkillSummary = z.infer<typeof SkillSummarySchema>;

export const SkillDetailSchema = SkillSummarySchema.extend({
  // Only the detail view renders the inventory, so only it receives one.
  files: SkillFileMetaSchema.array(),
  content: z.string(),
  attached_datamate_ids: z.number().array(),
});
export type SkillDetail = z.infer<typeof SkillDetailSchema>;

/**
 * The library list is paginated, matching the `Page[...]` envelope the rest of
 * the API returns: `{ items, page, pages, size, total }`.
 */
export const SkillListResponseSchema = pageSchema(SkillSummarySchema);

/** Rows per page. The API's own ceiling is higher; this is the UI default. */
export const DEFAULT_SKILL_PAGE_SIZE = 20;

/**
 * Query for one page of the library.
 *
 * `privacy` and `datamateId` are server-side filters. They have to be: the
 * scope tabs used to filter the rows already fetched, which narrows the screen
 * without narrowing the query — so a tab could read "no skills" while matching
 * ones sat on the next page.
 */
export interface SkillListParams {
  /** 1-indexed, matching the API envelope. */
  page?: number;
  size?: number;
  privacy?: SkillPrivacy;
  /** Only skills attached to this workspace. */
  datamateId?: number;
  /** `true`: only skills forked from the Altimate catalog. Server-side, like the rest. */
  imported?: boolean;
}

/**
 * A fixed set of skills one request just created — the catalog import result.
 * Not paginated: its size is the caller's own selection.
 */
export const SkillsCreatedResponseSchema = z.object({
  skills: SkillSummarySchema.array(),
});
/**
 * The result of replacing a skill's workspace attachments.
 *
 * Shaped like what the endpoint changed rather than like the whole skill:
 * attaching does not touch the bundle, and returning the skill forced the
 * server to read `SKILL.md` from S3 after the write had already committed.
 */
export const SkillWorkspacesResponseSchema = z.object({
  public_id: z.string(),
  attached_datamate_ids: z.number().array(),
});

export const SkillResponseSchema = z.object({
  skill: SkillDetailSchema,
});

/** The public catalog repository; the only one whose files can be linked to. */
export const SKILL_CATALOG_REPO = "AltimateAI/data-engineering-skills";

/**
 * One skill in Altimate's catalog, which merges the public
 * `AltimateAI/data-engineering-skills` with the private
 * `AltimateAI/data-engineering-skills-internal`.
 *
 * `(repo, catalog_id)` is the identity: a path alone does not say which repo it
 * is in. `repo` and `commit_sha` are absent from a backend that predates the
 * internal repo; `catalogSkillKey` reads a missing `repo` as the public one.
 * (A defaulted schema field would not help: `api.get` without query params
 * returns the raw body unparsed.)
 *
 * `slug` is the name in the skill's frontmatter — its identity in the agent's
 * namespace, and what collides there — so it is what the Browse list shows and
 * what an import is named by default.
 *
 * `summary` is the leading sentence of `description`. The full value is 400-850
 * characters of trigger prose ("Use when the task mentions..."), which reads as
 * documentation in a preview but not as a subtitle in a list row.
 *
 * `imported_public_id` is set when the caller already imported this entry. It
 * is matched on the upstream repo and path rather than the name, so it survives
 * the user renaming their copy.
 */
export const CatalogSkillSchema = z.object({
  repo: z.string().optional(),
  commit_sha: z.string().optional(),
  catalog_id: z.string(),
  slug: z.string(),
  summary: z.string(),
  description: z.string(),
  // A count, not the inventory — and no bundle bytes. The catalog list carries
  // metadata only: importing is server-side, so the client never sends a
  // bundle, and shipping every skill's `SKILL.md` made this response grow with
  // the whole upstream repo for a card that renders a name and a size.
  file_count: z.number(),
  size: z.number(),
  imported_public_id: z.string().nullable().optional(),
});
export type CatalogSkill = z.infer<typeof CatalogSkillSchema>;

/** A catalog skill's repo; one from a backend that sends none is public. */
export const catalogSkillRepo = (skill: { repo?: string }): string =>
  skill.repo ?? SKILL_CATALOG_REPO;

/** A catalog skill's identity across repos, e.g. for row ids and lookups. */
export const catalogSkillKey = (skill: { repo?: string; catalog_id: string }): string =>
  `${catalogSkillRepo(skill)}:${skill.catalog_id}`;

/** A curated group of catalog skills, as the repo's `marketplace.json` names them. */
export const CatalogGroupSchema = z.object({
  name: z.string(),
  description: z.string(),
  skills: CatalogSkillSchema.array(),
});
export type CatalogGroup = z.infer<typeof CatalogGroupSchema>;

export const SkillCatalogResponseSchema = z.object({
  commit_sha: z.string(),
  groups: CatalogGroupSchema.array(),
});
export type SkillCatalog = z.infer<typeof SkillCatalogResponseSchema>;

export interface ImportCatalogSkillsPayload {
  items: { catalog_id: string; repo?: string; name?: string }[];
  privacy: SkillPrivacy;
  datamate_ids: number[];
}

/**
 * The result of replacing a workspace's skill set.
 *
 * Shaped like what changed, mirroring `SkillWorkspacesResponseSchema`: the
 * workspace's own fields are untouched by an attachment write.
 */
export const DatamateSkillsResponseSchema = z.object({
  datamate_id: z.number(),
  attached_skill_public_ids: z.string().array(),
});

/**
 * How many skill ids one attachment request may carry, mirroring the API's
 * `max_length` on that payload.
 *
 * Not a bound on how many skills a workspace *accumulates*: nothing on the
 * server enforces that, so 101 skills each attaching themselves to one
 * workspace is legal with no single request exceeding this. The attached list
 * therefore cannot be assumed to fit one page — see `useDatamateSkills`, which
 * reports whether it did.
 */
export const MAX_ATTACHED_SKILLS = 100;

export interface CreateSkillPayload {
  name: string;
  description?: string;
  files: SkillFile[];
  privacy?: SkillPrivacy;
}

export interface UpdateSkillPayload {
  name?: string;
  description?: string;
  files?: SkillFile[];
  privacy?: SkillPrivacy;
}

// The scope chip in the list.
export type SkillScope = "private" | "org";

export const skillScope = (skill: SkillSummary): SkillScope =>
  skill.privacy === "public" ? "org" : "private";

/**
 * The Scope control's two options. Labels are the user-facing wording; the
 * values are the `privacy` the API stores.
 */
export const SKILL_SCOPE_OPTIONS = [
  {
    value: "private" as SkillPrivacy,
    label: "Private to workspace",
    hint: "Only you can see or attach this skill.",
  },
  {
    value: "public" as SkillPrivacy,
    label: "Share with organization",
    hint: "Anyone in your organization can see and attach this skill.",
  },
];

export const SKILL_SCOPE_LABEL: Record<SkillScope, string> = {
  private: "Private",
  org: "Org",
};
